<template>
  <div class="salon-app salon-register">
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.register.title") }}</h1>
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <div class="salon-progress-meta">
          <span class="salon-progress-label">{{ $t("salon.flow.stepOf", { step, total: totalSteps }) }}</span>
        </div>
      </div>
    </header>

    <main class="salon-main">
      <section class="salon-intro">
        <h2 class="salon-intro-title">{{ $t("salon.register.intro") }}</h2>
        <p class="salon-intro-subtitle">
          {{ $t("salon.register.subtitle") }}
        </p>
      </section>

      <form class="salon-form" novalidate @submit.prevent="submit">
        <div class="salon-field">
          <label class="salon-field-label" for="salon_full_name">{{ $t("salon.register.fullName") }}</label>
          <input
            id="salon_full_name"
            v-model="fullName"
            class="salon-input"
            type="text"
            name="salon_full_name"
            autocomplete="name"
            :placeholder="$t('salon.register.fullNamePlaceholder')"
          />
        </div>

        <div class="salon-field">
          <label class="salon-field-label" for="salon_email">{{ $t("salon.register.email") }}</label>
          <input
            id="salon_email"
            v-model="email"
            class="salon-input"
            type="email"
            name="salon_email"
            autocomplete="email"
            :placeholder="$t('salon.register.emailPlaceholder')"
          />
          <p class="salon-field-hint">{{ $t("salon.register.emailHint") }}</p>
        </div>

        <!-- Carried over from the sign-in step and deliberately not editable: it is the
             number the customer already proved they were using. -->
        <div class="salon-field">
          <label class="salon-field-label" for="salon_mobile">{{ $t("salon.register.mobile") }}</label>
          <div class="salon-locked">
            <input
              id="salon_mobile"
              class="salon-input salon-input--locked"
              type="tel"
              name="salon_mobile"
              readonly
              :value="phoneText"
            />
            <svg class="salon-locked-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 10V8a6 6 0 1112 0v2m-13 0h14v10H5V10z" fill="none" stroke="currentColor"
                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>

        <div class="salon-field">
          <label id="salon_pin_label" class="salon-field-label">{{ $t("salon.register.choosePin") }}</label>
          <SalonPinInput
            v-model="pin"
            :length="pinLength"
            name="salon_new_pin"
            label="PIN"
            labelledby="salon_pin_label"
          />
          <p class="salon-field-hint">
            {{ $t("salon.register.pinHint", { length: pinLength }) }}
          </p>
        </div>

        <p v-if="errorMessage" class="salon-error" role="alert">{{ errorMessage }}</p>

        <label class="salon-check">
          <input v-model="agreed" class="salon-check-input" type="checkbox" name="salon_terms" />
          <span class="salon-check-box" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span class="salon-check-label">
            <i18n-t keypath="salon.register.agree" tag="span" scope="global">
              <template #terms>
                <button class="salon-link" type="button" @click.prevent.stop="showTerms = true">{{ $t("salon.register.terms") }}</button>
              </template>
              <template #privacy>
                <button class="salon-link" type="button" @click.prevent.stop="showTerms = true">{{ $t("salon.register.privacy") }}</button>
              </template>
            </i18n-t>
          </span>
        </label>
      </form>
    </main>

    <nav class="salon-cta">
      <button class="salon-cta-btn" type="button" :disabled="!canSubmit || isSaving" @click="submit">
        <span>{{ $t("salon.register.submit") }}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M5 12h14m-6-6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </nav>

    <!-- The PDPA text MOO already shows at sign-up (components/TermService.vue), rendered
         from the same i18n keys but in the salon's own styling. Deliberately not that
         component: it is a Bootstrap modal, and leaving one open across a step change
         strands its backdrop over the page. -->
    <div v-if="showTerms" class="salon-sheet" role="dialog" aria-modal="true" aria-labelledby="salon_terms_title">
      <div class="salon-sheet-backdrop" @click="showTerms = false"></div>
      <div class="salon-sheet-panel">
        <div class="salon-sheet-head">
          <h3 id="salon_terms_title" class="salon-sheet-title">{{ $t("signUp.pdpaTitle") }}</h3>
          <button class="salon-sheet-close" type="button" :aria-label="$t('salon.register.close')" @click="showTerms = false">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div class="salon-sheet-body">
          <p class="salon-sheet-intro">{{ $t("signUp.pdpaIntro") }}</p>
          <p v-for="key in pdpaParagraphs" :key="key">{{ $t("signUp." + key, { brand: brandName }) }}</p>
        </div>
        <div class="salon-sheet-foot">
          <button class="salon-sheet-btn" type="button" @click="acceptTerms">
            {{ $t("signUp.pdpaContinue") }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="isSaving" class="salon-overlay">
      <div class="salon-overlay-card" role="status" aria-live="polite">
        <svg class="salon-spinner" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="salon-spinner-path" cx="25" cy="25" r="20" fill="none" stroke="currentColor"
            stroke-width="4" stroke-linecap="round" stroke-dasharray="90 150" />
        </svg>
        <h3 class="salon-overlay-title">{{ $t("salon.register.creating") }}</h3>
        <p class="salon-overlay-phone">{{ phoneText }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import SalonPinInput from "@/salon/components/PinInput.vue";
import MemberAuthService from "@/services/memberAuth.service";
import { localStorageName } from "@/config/utils";
import "@/salon/styles/tokens.css";

// SH003 (Salon_UI/register_profile) — a customer the salon has never seen before names
// themselves and picks a PIN. The reference screen has no PIN field; it is added here
// because the salon signs in with phone + PIN, so one has to be chosen at sign-up.
const STEP = 3;
const TOTAL_STEPS = 9;
const PIN_LENGTH = 4;
// Deliberately loose: the API validates properly, and a stricter pattern here mostly
// rejects legitimate addresses.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default {
  name: "SalonRegister",
  components: {
    SalonPinInput,
  },
  props: {
    phoneNumber: {
      type: String,
      default: "",
    },
    phoneCode: {
      type: String,
      default: "65",
    },
  },
  emits: ["back", "registered"],
  data() {
    return {
      step: STEP,
      totalSteps: TOTAL_STEPS,
      pinLength: PIN_LENGTH,
      fullName: "",
      email: "",
      pin: "",
      agreed: false,
      showTerms: false,
      isSaving: false,
      errorMessage: "",
    };
  },
  computed: {
    progressWidth() {
      return ((STEP / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },
    phoneText() {
      const digits = String(this.phoneNumber || "").replace(/\D/g, "");
      const grouped = digits.length > 4 ? digits.slice(0, 4) + " " + digits.slice(4) : digits;
      return "+" + this.phoneCode + " " + grouped;
    },
    pdpaParagraphs() {
      return ["pdpaBody1", "pdpaBody2", "pdpaBody3", "pdpaBody4"];
    },
    // The PDPA text names the merchant throughout; App.vue stores the brand name from
    // /check-merchant-and-branch. "the salon" keeps the sentences readable if it is unset.
    brandName() {
      return localStorage.getItem(localStorageName("MERCHANT_BRAND_NAME")) || this.$t("salon.register.brandFallback");
    },
    canSubmit() {
      return (
        this.fullName.trim() !== "" &&
        EMAIL_PATTERN.test(this.email.trim()) &&
        this.pin.length === PIN_LENGTH &&
        this.agreed
      );
    },
  },
  watch: {
    fullName() {
      this.errorMessage = "";
    },
    email() {
      this.errorMessage = "";
    },
    pin() {
      this.errorMessage = "";
    },
  },
  methods: {
    // Reaching the end of the notice and pressing Continue is the consent action, so the
    // customer does not have to close the sheet and hunt for the checkbox.
    acceptTerms() {
      this.agreed = true;
      this.showTerms = false;
    },

    async submit() {
      if (!this.canSubmit || this.isSaving) {
        return;
      }

      this.errorMessage = "";
      this.isSaving = true;

      try {
        const data = await MemberAuthService.salonRegister({
          name: this.fullName.trim(),
          email: this.email.trim(),
          phone_number: String(this.phoneNumber).replace(/\D/g, ""),
          phone_code: this.phoneCode,
          passcode: this.pin,
          i_agree: 1,
        });

        if (!data.result.isSuccess) {
          this.errorMessage = data.result.message || this.$t("salon.register.createError");
          return;
        }

        this.$emit("registered", {
          user: data.data.user,
          phone_number: String(this.phoneNumber).replace(/\D/g, ""),
          phone_code: this.phoneCode,
        });
      } catch (error) {
        this.errorMessage = this.$t("salon.register.networkError");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-register {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--salon-primary);
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-progress {
  margin-top: 16px;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-container-highest);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary-container);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-meta {
  margin-top: 8px;
  text-align: center;
}

.salon-progress-label {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.06em;
  color: var(--salon-on-surface-variant);
  text-transform: uppercase;
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-lg);
  padding: var(--salon-stack-lg) var(--salon-container-padding) 150px;
}

.salon-intro {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
  text-align: center;
}

.salon-intro-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-on-surface);
}

.salon-intro-subtitle {
  max-width: 24rem;
  margin: 0 auto;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Form ──────────────────────────────────────────────────────── */
.salon-form {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
}

.salon-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.salon-field-label {
  margin-left: 4px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.06em;
  color: var(--salon-on-surface-variant);
  text-transform: uppercase;
}

.salon-field-hint {
  margin: 0 0 0 4px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  line-height: 24px;
  color: var(--salon-on-surface);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: var(--salon-radius-input);
  box-shadow: var(--salon-shadow-soft);
  outline: none;
  appearance: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.salon-input::placeholder {
  color: var(--salon-on-surface-variant);
  opacity: 0.7;
}

.salon-input:focus {
  border-color: var(--salon-primary);
  box-shadow: 0 0 0 1px var(--salon-primary);
}

.salon-locked {
  position: relative;
  display: flex;
  align-items: center;
}

.salon-input--locked {
  padding-right: 44px;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-container);
  cursor: not-allowed;
  box-shadow: none;
  user-select: none;
}

.salon-input--locked:focus {
  border-color: var(--salon-surface-container-highest);
  box-shadow: none;
}

.salon-locked-icon {
  position: absolute;
  right: 16px;
  color: var(--salon-on-surface-variant);
  opacity: 0.6;
  pointer-events: none;
}

.salon-error {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-error);
}

/* ── Terms checkbox ────────────────────────────────────────────── */
.salon-check {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-top: 4px;
  cursor: pointer;
}

.salon-check-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.salon-check-box {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  color: transparent;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 4px;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.salon-check-input:checked + .salon-check-box {
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border-color: var(--salon-primary);
}

.salon-check-input:focus-visible + .salon-check-box {
  box-shadow: 0 0 0 2px var(--salon-secondary);
}

.salon-check-label {
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-link {
  padding: 0;
  font: inherit;
  color: var(--salon-primary);
  background: none;
  border: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  border-top: 1px solid var(--salon-surface-variant);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-variant);
  cursor: not-allowed;
  opacity: 0.7;
}

/* ── Saving overlay ────────────────────────────────────────────── */
.salon-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
}

.salon-overlay-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 280px;
  padding: 24px;
  text-align: center;
  background: var(--salon-surface-lowest);
  border-radius: var(--salon-radius-card);
  box-shadow: 0 4px 20px rgba(28, 28, 30, 0.08);
}

.salon-spinner {
  width: 40px;
  height: 40px;
  margin-bottom: 20px;
  color: var(--salon-secondary-container);
  animation: salon-spin 2s linear infinite;
}

.salon-spinner-path {
  animation: salon-dash 1.5s ease-in-out infinite;
}

.salon-overlay-title {
  margin: 0 0 4px;
  /* material-dashboard forces Roboto Slab onto every h1-h3. */
  font-family: var(--salon-font-body);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-overlay-phone {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
}

/* ── Terms sheet ───────────────────────────────────────────────── */
.salon-sheet {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.salon-sheet-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}

.salon-sheet-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--salon-max-width);
  max-height: 85vh;
  background: var(--salon-surface-lowest);
  border-radius: var(--salon-radius-card) var(--salon-radius-card) 0 0;
  box-shadow: 0 -4px 24px rgba(28, 28, 30, 0.16);
}

.salon-sheet-head {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 20px var(--salon-container-padding) 12px;
  border-bottom: 1px solid var(--salon-surface-variant);
}

.salon-sheet-title {
  margin: 0;
  /* material-dashboard forces Roboto Slab onto every h1-h3. */
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-sheet-close {
  display: flex;
  flex-shrink: 0;
  padding: 4px;
  color: var(--salon-on-surface-variant);
  background: none;
  border: 0;
  cursor: pointer;
}

.salon-sheet-body {
  flex: 1;
  padding: 16px var(--salon-container-padding);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.salon-sheet-body p {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 21px;
  color: var(--salon-on-surface-variant);
}

.salon-sheet-intro {
  font-weight: 500;
  color: var(--salon-on-surface) !important;
}

.salon-sheet-foot {
  padding: 12px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--salon-surface-variant);
}

.salon-sheet-btn {
  width: 100%;
  padding: 14px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

@keyframes salon-spin {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes salon-dash {
  0% {
    stroke-dasharray: 1 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90 150;
    stroke-dashoffset: -124;
  }
}

@media (prefers-reduced-motion: reduce) {
  .salon-spinner,
  .salon-spinner-path {
    animation: none;
  }
}
</style>
